import React from "react";
import { motion } from "framer-motion";
import { popSuccess, useMotionPrefs, withMotionPrefs } from "@/lib/motion";
import MotionCard from "@/components/motion/MotionCard";
import { useI18n } from "@/contexts/I18nContext";

export function StatCard({ icon: Icon, label, value, accent = "#071A33", testId, sub }) {
  const { isReduced } = useMotionPrefs();
  const valueVariants = withMotionPrefs(popSuccess, isReduced);

  return (
    <MotionCard
      data-testid={testId}
      className="rp-card rp-card-hover p-5 flex items-start justify-between gap-4"
    >
      <div className="min-w-0">
        <div className="text-sm text-gray-500 mb-2">{label}</div>
        <motion.div
          key={String(value)}
          className="text-2xl md:text-3xl font-bold text-gray-900 tabular font-heading truncate"
          variants={valueVariants}
          initial="hidden"
          animate="show"
        >
          {value}
        </motion.div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </div>
      {Icon && (
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon className="w-6 h-6" />
        </div>
      )}
    </MotionCard>
  );
}

export function StatusBadge({ status, label }) {
  const map = {
    available: "bg-emerald-50 text-emerald-700 border-emerald-100",
    occupied: "bg-amber-50 text-amber-700 border-amber-100",
    reserved: "bg-sky-50 text-sky-700 border-sky-100",
    cleaning: "bg-gray-100 text-gray-600 border-gray-200",
    draft: "bg-gray-100 text-gray-600 border-gray-200",
    sent: "bg-sky-50 text-sky-700 border-sky-100",
    new: "bg-sky-50 text-sky-700 border-sky-100",
    preparing: "bg-amber-50 text-amber-700 border-amber-100",
    ready: "bg-emerald-50 text-emerald-700 border-emerald-100",
    served: "bg-emerald-50 text-emerald-700 border-emerald-100",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-100",
    unpaid: "bg-rose-50 text-rose-700 border-rose-100",
    debt: "bg-orange-50 text-orange-700 border-orange-100",
    cancelled: "bg-gray-100 text-gray-500 border-gray-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-100",
    partial: "bg-amber-50 text-amber-700 border-amber-100",
    overdue: "bg-rose-50 text-rose-700 border-rose-100",
  };
  const cls = map[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${cls}`}
    >
      <span
        className="status-dot"
        style={{
          backgroundColor:
            status === "available" || status === "ready" || status === "served" || status === "paid" || status === "active"
              ? "#16A34A"
              : status === "occupied" || status === "preparing" || status === "partial"
              ? "#F59E0B"
              : status === "debt"
              ? "#F97316"
              : status === "unpaid" || status === "overdue"
              ? "#DC2626"
              : status === "sent" || status === "new" || status === "reserved"
              ? "#0EA5E9"
              : "#9CA3AF",
        }}
      />
      {label}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, sub }) {
  const { isReduced } = useMotionPrefs();
  const iconVariants = withMotionPrefs(
    {
      hidden: { opacity: 0, y: 8, scale: 0.95 },
      show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
    },
    isReduced,
  );

  return (
    <div className="text-center py-14 px-6">
      {Icon && (
        <motion.div
          className="mx-auto w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 grid place-items-center text-gray-400 mb-4"
          variants={iconVariants}
          initial="hidden"
          animate="show"
        >
          <Icon className="w-6 h-6" />
        </motion.div>
      )}
      <div className="text-gray-700 font-medium mb-1">{title}</div>
      {sub && <div className="text-sm text-gray-500">{sub}</div>}
    </div>
  );
}

export function Section({ title, action, children }) {
  return (
    <MotionCard className="rp-card p-5 md:p-6">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h3 className="text-lg font-bold text-gray-900 font-heading">{title}</h3>
        {action}
      </div>
      {children}
    </MotionCard>
  );
}

export function LoadError({ message, onRetry }) {
  const { t } = useI18n();
  return (
    <div className="rp-card p-8 text-center space-y-4 max-w-lg mx-auto my-10">
      <div className="text-gray-700 font-medium">{message || t("loadError")}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-[#071A33] text-white text-sm font-medium"
        >
          {t("retry")}
        </button>
      )}
    </div>
  );
}
